import * as THREE from "three";
import type { Vec3 } from "@rietz/shared";
import {
  EXPLOSION_CORE_COLOR,
  EXPLOSION_LIGHT_COLOR,
  EXPLOSION_SPARK_COLOR,
  GLASS_PANE_COLOR,
  IMPACT_DUST_COLOR,
  MUZZLE_FLASH_COLOR,
  MUZZLE_LIGHT_COLOR,
  SMOKE_COLOR,
  TRACER_HEAD_COLOR,
} from "../render/palette.js";

/**
 * Combat effects: rounds in flight, muzzle flash, wall impacts, explosions,
 * smoke, breaking glass. Fixed pools, never allocation — when a pool is full
 * the OLDEST live effect is stolen, since dropping the new one would make a
 * burst appear to stop firing. Bullets and flash are additive (black contributes nothing, one
 * draw call, no sorting); dust and smoke are ordinary transparent sprites
 * because they must darken what is behind them.
 *
 * A bullet slot has an owner: a travelling round (`game/bullets.ts`) CLAIMS a
 * slot, rewrites its position and facing every frame, and RELEASES it on
 * landing, at which point the bolt disappears — the dust puff is the landing
 * cue. The pool knows nothing about the projectile simulation.
 *
 * No DOM, so it constructs and updates under plain node.
 */

/** Concurrent rounds in the air. Eight players at 400 rpm never come close —
 *  and it is deliberately the same number as `game/bullets.ts`'s own pool. */
const MAX_BULLETS = 24;
/** Concurrent impact puffs. */
const MAX_IMPACTS = 16;

/** Impact puffs linger — they are the "where did it land" cue, and the only one
 *  left now that a spent round vanishes instead of fading. */
const IMPACT_LIFE_MS = 320;
/** Muzzle flash duration, ms. Two frames at 60 Hz. */
const FLASH_LIFE_MS = 34;

/**
 * The bolt, metres: a slim box along its flight axis, long enough to read as a
 * streak across the valley.
 */
const BULLET_WIDTH_M = 0.035;
const BULLET_LENGTH_M = 0.7;

/** Puff size at birth and at death, metres. */
const IMPACT_START_M = 0.08;
const IMPACT_END_M = 0.5;

/** Point-light punch on the gun while the flash lives. */
const MUZZLE_LIGHT_INTENSITY = 6;
const MUZZLE_LIGHT_RANGE_M = 4;

// --- Grenades (protocol v24) ------------------------------------------------

/** Concurrent explosions. A fifth steals the oldest, by then a fading puff. */
const MAX_EXPLOSIONS = 4;
/** Fragments thrown off each one. */
const EXPLOSION_SPARKS = 6;
const EXPLOSION_LIFE_MS = 420;
/** The light dies well before the smoke does — a blast is a strobe, not a fire. */
const EXPLOSION_LIGHT_MS = 250;
const EXPLOSION_LIGHT_INTENSITY = 40;
const EXPLOSION_LIGHT_RANGE_M = 18;
const EXPLOSION_CORE_START_M = 0.6;
const EXPLOSION_CORE_END_M = 5;
const SPARK_SIZE_M = 0.35;
const SPARK_SPEED_MPS = 16;
/** Sparks arc rather than flying straight — cosmetic, not the shared gravity. */
const SPARK_GRAVITY = 22;

// --- Breaking glass ---------------------------------------------------------

/** Concurrent shatters. A window is a one-off; three at once is a firefight. */
const MAX_SHATTERS = 3;
/** Shards per burst. */
const SHATTER_SHARDS = 14;
const SHATTER_LIFE_MS = 900;
/** Shard size range, metres — splinters, not plates. */
const SHARD_MIN_M = 0.04;
const SHARD_MAX_M = 0.08;
/** Speed through the pane, metres per second — both ways, hence the sign. */
const SHARD_NORMAL_MIN_MPS = 1;
const SHARD_NORMAL_MAX_MPS = 3;
/** ...and sideways/up, which is what makes it a burst rather than a jet. */
const SHARD_SIDE_MPS = 1.4;
/** Shards fall; the sparks' cosmetic gravity, a third as hard. */
const SHARD_GRAVITY = 8;
/** Radians per second of spin, ±. Glass glints as it turns. */
const SHARD_SPIN = 6;
const SHARD_OPACITY = 0.85;

/** Concurrent smoke clouds, and the sprites each one is built from. */
const MAX_SMOKE_CLOUDS = 3;
const SMOKE_PUFFS = 12;
/** Seconds a cloud takes to bloom to full size, and to fade at the end. */
const SMOKE_BLOOM_SEC = 1.2;
const SMOKE_FADE_SEC = 2.5;
/** Per-sprite opacity at full strength. Twelve of them overlap into a wall. */
const SMOKE_OPACITY = 0.5;

interface Impact {
  life: number;
  born: number;
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
}

/** One blast: a growing core, six sparks thrown off it, and a stamp for the
 *  recycling order. The light is shared — see `explosionLight`. */
interface Explosion {
  life: number;
  born: number;
  origin: THREE.Vector3;
  core: THREE.Sprite;
  coreMat: THREE.SpriteMaterial;
  sparks: THREE.Sprite[];
  sparkMats: THREE.SpriteMaterial[];
  /** Launch direction × speed for each spark, metres per second. */
  sparkVel: { x: number; y: number; z: number }[];
}

/** One burst: fourteen shards thrown off a pane, on their own clock. */
interface Shatter {
  life: number;
  born: number;
  sprites: THREE.Sprite[];
  materials: THREE.SpriteMaterial[];
  /** Metres per second per shard, and radians per second of spin. */
  vel: { x: number; y: number; z: number }[];
  spin: number[];
}

/** One cloud: twelve drifting sprites and the clock they live on. */
interface SmokeCloud {
  /** Seconds left. `<= 0` means the slot is free. */
  life: number;
  totalSec: number;
  born: number;
  radiusM: number;
  sprites: THREE.Sprite[];
  materials: THREE.SpriteMaterial[];
  /** Metres per second of drift for each puff — slow, and mostly upward. */
  drift: { x: number; y: number; z: number }[];
}

export interface FxOptions {
  /** Bullets and impacts are drawn here, in world space. */
  scene: THREE.Scene;
  /**
   * Muzzle anchor in the VIEWMODEL scene; the flash is parented to it and
   * inherits recoil. Optional for headless callers.
   */
  muzzle?: THREE.Object3D;
}

export class Fx {
  private readonly scene: THREE.Scene;

  // --- Bullets: one InstancedMesh, MAX_BULLETS bolts ------------------------
  private readonly bulletGeo: THREE.BoxGeometry;
  private readonly bulletMat: THREE.MeshBasicMaterial;
  private readonly bulletMesh: THREE.InstancedMesh;
  /** `true` while a round in flight is driving instance `i`. */
  private readonly bulletOwned: boolean[] = [];

  // Scratch for `bulletSet`, which runs once per live round per frame. Nothing
  // in that path may allocate.
  private readonly sPos = new THREE.Vector3();
  private readonly sDir = new THREE.Vector3();
  private readonly sAxis = new THREE.Vector3(0, 0, -1);
  private readonly sQuat = new THREE.Quaternion();
  private readonly sScale = new THREE.Vector3(1, 1, 1);
  private readonly sMat = new THREE.Matrix4();

  // --- Impacts --------------------------------------------------------------
  private readonly impactRoot = new THREE.Group();
  private readonly impacts: Impact[] = [];
  private readonly dustTexture: THREE.Texture | null;

  // --- Explosions and smoke (protocol v24) ----------------------------------
  private readonly blastRoot = new THREE.Group();
  private readonly explosions: Explosion[] = [];
  /**
   * ONE point light for every explosion, parked at the most recent — a light is
   * compiled into every material's shader even when off, and two grenades a
   * quarter second apart cannot be told apart.
   */
  private readonly explosionLight: THREE.PointLight;
  private explosionLightLife = 0;

  private readonly smokeRoot = new THREE.Group();
  private readonly smoke: SmokeCloud[] = [];

  // --- Breaking glass -------------------------------------------------------
  private readonly shardRoot = new THREE.Group();
  private readonly shatters: Shatter[] = [];

  // --- Muzzle flash ---------------------------------------------------------
  private readonly flashSprite: THREE.Sprite | null = null;
  private readonly flashMat: THREE.SpriteMaterial | null = null;
  private readonly flashLight: THREE.PointLight | null = null;
  private flashLife = 0;

  /** Monotonic counter used only to order recycling. */
  private stamp = 0;

  constructor(opts: FxOptions) {
    this.scene = opts.scene;

    // --- Bullets: long axis Z, −Z is the nose, so facing is one
    // `setFromUnitVectors`.
    this.bulletGeo = new THREE.BoxGeometry(
      BULLET_WIDTH_M,
      BULLET_WIDTH_M,
      BULLET_LENGTH_M,
    );
    this.bulletMat = new THREE.MeshBasicMaterial({
      color: TRACER_HEAD_COLOR,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      // Unlit and never fogged: a tracer BURNS, and long-range fire must stay
      // visible through the haze.
      fog: false,
    });
    this.bulletMesh = new THREE.InstancedMesh(
      this.bulletGeo,
      this.bulletMat,
      MAX_BULLETS,
    );
    this.bulletMesh.name = "fx:bullets";
    // The instances are scattered across the whole world every frame; a
    // bounding sphere would be permanently wrong.
    this.bulletMesh.frustumCulled = false;
    this.bulletMesh.renderOrder = 5;
    this.bulletMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Zero scale is how a slot is "off": no visible/invisible flag per instance
    // exists, and a degenerate matrix costs the rasteriser nothing.
    this.sMat.makeScale(0, 0, 0);
    for (let i = 0; i < MAX_BULLETS; i++) {
      this.bulletMesh.setMatrixAt(i, this.sMat);
      this.bulletOwned.push(false);
    }
    this.bulletMesh.instanceMatrix.needsUpdate = true;
    this.scene.add(this.bulletMesh);

    // --- Impacts -----------------------------------------------------------
    this.impactRoot.name = "fx:impacts";
    this.scene.add(this.impactRoot);
    this.dustTexture = makeSoftDotTexture();

    for (let i = 0; i < MAX_IMPACTS; i++) {
      const material = new THREE.SpriteMaterial({
        color: IMPACT_DUST_COLOR,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        fog: false,
      });
      if (this.dustTexture !== null) material.map = this.dustTexture;
      const sprite = new THREE.Sprite(material);
      sprite.visible = false;
      this.impactRoot.add(sprite);
      this.impacts.push({ life: 0, born: 0, sprite, material });
    }

    // --- Explosions: preallocated like the impacts.
    this.blastRoot.name = "fx:explosions";
    this.scene.add(this.blastRoot);
    for (let i = 0; i < MAX_EXPLOSIONS; i++) {
      const coreMat = new THREE.SpriteMaterial({
        color: EXPLOSION_CORE_COLOR,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
      });
      if (this.dustTexture !== null) coreMat.map = this.dustTexture;
      const core = new THREE.Sprite(coreMat);
      core.visible = false;
      this.blastRoot.add(core);

      const sparks: THREE.Sprite[] = [];
      const sparkMats: THREE.SpriteMaterial[] = [];
      const sparkVel: { x: number; y: number; z: number }[] = [];
      for (let s = 0; s < EXPLOSION_SPARKS; s++) {
        const mat = new THREE.SpriteMaterial({
          color: EXPLOSION_SPARK_COLOR,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
          fog: false,
        });
        if (this.dustTexture !== null) mat.map = this.dustTexture;
        const sprite = new THREE.Sprite(mat);
        sprite.scale.setScalar(SPARK_SIZE_M);
        sprite.visible = false;
        this.blastRoot.add(sprite);
        sparks.push(sprite);
        sparkMats.push(mat);
        sparkVel.push({ x: 0, y: 0, z: 0 });
      }

      this.explosions.push({
        life: 0,
        born: 0,
        origin: new THREE.Vector3(),
        core,
        coreMat,
        sparks,
        sparkMats,
        sparkVel,
      });
    }
    this.explosionLight = new THREE.PointLight(
      EXPLOSION_LIGHT_COLOR,
      0,
      EXPLOSION_LIGHT_RANGE_M,
    );
    this.blastRoot.add(this.explosionLight);

    // --- Smoke: ordinary transparent sprites, not additive — smoke HIDES what
    // is
    // behind it. `depthWrite: false` so the puffs of one cloud do not carve
    // holes
    // in each other.
    this.smokeRoot.name = "fx:smoke";
    this.scene.add(this.smokeRoot);
    for (let i = 0; i < MAX_SMOKE_CLOUDS; i++) {
      const sprites: THREE.Sprite[] = [];
      const materials: THREE.SpriteMaterial[] = [];
      const drift: { x: number; y: number; z: number }[] = [];
      for (let p = 0; p < SMOKE_PUFFS; p++) {
        const mat = new THREE.SpriteMaterial({
          color: SMOKE_COLOR,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          fog: false,
        });
        if (this.dustTexture !== null) mat.map = this.dustTexture;
        const sprite = new THREE.Sprite(mat);
        sprite.visible = false;
        this.smokeRoot.add(sprite);
        sprites.push(sprite);
        materials.push(mat);
        drift.push({ x: 0, y: 0, z: 0 });
      }
      this.smoke.push({
        life: 0,
        totalSec: 1,
        born: 0,
        radiusM: 1,
        sprites,
        materials,
        drift,
      });
    }

    // --- Breaking glass: ordinary transparent sprites, like the smoke — a
    // shard REFLECTS, it does not glow, and additive over a bright facade
    // would read as sparks.
    this.shardRoot.name = "fx:shards";
    this.scene.add(this.shardRoot);
    for (let i = 0; i < MAX_SHATTERS; i++) {
      const sprites: THREE.Sprite[] = [];
      const materials: THREE.SpriteMaterial[] = [];
      const vel: { x: number; y: number; z: number }[] = [];
      const spin: number[] = [];
      for (let s = 0; s < SHATTER_SHARDS; s++) {
        const mat = new THREE.SpriteMaterial({
          color: GLASS_PANE_COLOR,
          transparent: true,
          opacity: 0,
          depthWrite: false,
          fog: false,
        });
        if (this.dustTexture !== null) mat.map = this.dustTexture;
        const sprite = new THREE.Sprite(mat);
        sprite.visible = false;
        this.shardRoot.add(sprite);
        sprites.push(sprite);
        materials.push(mat);
        vel.push({ x: 0, y: 0, z: 0 });
        spin.push(0);
      }
      this.shatters.push({ life: 0, born: 0, sprites, materials, vel, spin });
    }

    // --- Muzzle flash ------------------------------------------------------
    const muzzle = opts.muzzle;
    if (muzzle !== undefined) {
      const mat = new THREE.SpriteMaterial({
        color: MUZZLE_FLASH_COLOR,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        fog: false,
      });
      if (this.dustTexture !== null) mat.map = this.dustTexture;
      const sprite = new THREE.Sprite(mat);
      sprite.scale.setScalar(0.16);
      sprite.visible = false;
      muzzle.add(sprite);

      const light = new THREE.PointLight(
        MUZZLE_LIGHT_COLOR,
        0,
        MUZZLE_LIGHT_RANGE_M,
      );
      muzzle.add(light);

      this.flashSprite = sprite;
      this.flashMat = mat;
      this.flashLight = light;
    }
  }

  /**
   * Takes a bullet slot for a round about to fly. An owned slot is NEVER
   * stolen; `-1` when every slot is owned — unreachable while `game/bullets.ts`
   * sizes its pool to `MAX_BULLETS`, and it means the round flies invisibly.
   * Nothing shows until the first `bulletSet`.
   */
  bulletClaim(): number {
    for (let i = 0; i < MAX_BULLETS; i++) {
      if (this.bulletOwned[i] === true) continue;
      this.bulletOwned[i] = true;
      return i;
    }
    return -1;
  }

  /**
   * Puts a claimed round at `pos`, nosed along the UNIT direction `dir`. World
   * space; no-op for `-1`. Once per live round per frame — everything touched
   * is scratch.
   */
  bulletSet(i: number, pos: Vec3, dir: Vec3): void {
    if (i < 0) return;
    this.sPos.set(pos.x, pos.y, pos.z);
    this.sDir.set(dir.x, dir.y, dir.z);
    this.sQuat.setFromUnitVectors(this.sAxis, this.sDir);
    this.sMat.compose(this.sPos, this.sQuat, this.sScale);
    this.bulletMesh.setMatrixAt(i, this.sMat);
    this.bulletMesh.instanceMatrix.needsUpdate = true;
  }

  /** Hands a bullet slot back: the bolt vanishes on the spot. */
  bulletRelease(i: number): void {
    if (i < 0) return;
    this.bulletOwned[i] = false;
    this.sMat.makeScale(0, 0, 0);
    this.bulletMesh.setMatrixAt(i, this.sMat);
    this.bulletMesh.instanceMatrix.needsUpdate = true;
  }

  /** A round stopping on geometry: a small puff of dust that expands and fades. */
  impact(at: Vec3): void {
    const i = this.claimImpact();
    const im = this.impacts[i];
    if (im === undefined) return;
    im.life = IMPACT_LIFE_MS;
    im.born = ++this.stamp;
    im.sprite.position.set(at.x, at.y, at.z);
    im.sprite.scale.setScalar(IMPACT_START_M);
    im.sprite.visible = true;
    im.material.opacity = 0.75;
  }

  /**
   * A grenade going off: a growing hot core, six fragments, a quarter second of
   * light. Position is the SERVER's — the client's arc was a guess.
   */
  explosion(at: Vec3): void {
    const i = this.claimExplosion();
    const ex = this.explosions[i];
    if (ex === undefined) return;

    ex.life = EXPLOSION_LIFE_MS;
    ex.born = ++this.stamp;
    ex.origin.set(at.x, at.y, at.z);
    ex.core.position.copy(ex.origin);
    ex.core.scale.setScalar(EXPLOSION_CORE_START_M);
    ex.core.visible = true;
    ex.coreMat.opacity = 1;

    for (let s = 0; s < ex.sparks.length; s++) {
      // Uniform over the UPPER hemisphere: fragments into the road are sprites
      // nobody sees.
      const phi = Math.random() * Math.PI * 2;
      const cosT = Math.random();
      const sinT = Math.sqrt(1 - cosT * cosT);
      const speed = SPARK_SPEED_MPS * (0.55 + Math.random() * 0.45);
      const vel = ex.sparkVel[s];
      if (vel !== undefined) {
        vel.x = Math.cos(phi) * sinT * speed;
        vel.y = cosT * speed;
        vel.z = Math.sin(phi) * sinT * speed;
      }
      const sprite = ex.sparks[s];
      const mat = ex.sparkMats[s];
      if (sprite !== undefined) {
        sprite.position.copy(ex.origin);
        sprite.visible = true;
      }
      if (mat !== undefined) mat.opacity = 1;
    }

    // The shared light moves to the newest blast and restarts — see the field.
    this.explosionLight.position.copy(ex.origin);
    this.explosionLightLife = EXPLOSION_LIGHT_MS;
    this.explosionLight.intensity = EXPLOSION_LIGHT_INTENSITY;
  }

  /**
   * A smoke cloud, `durationSec` long and `radiusM` across — the numbers from
   * the item def, so the player sees the volume the SERVER blocks sight lines
   * with. Twelve drifting sprites rather than one decal-like puff.
   */
  smokeCloud(at: Vec3, radiusM: number, durationSec: number): void {
    const i = this.claimSmoke();
    const cloud = this.smoke[i];
    if (cloud === undefined) return;

    cloud.life = durationSec;
    cloud.totalSec = durationSec;
    cloud.born = ++this.stamp;
    cloud.radiusM = radiusM;

    for (let p = 0; p < cloud.sprites.length; p++) {
      const sprite = cloud.sprites[p];
      const drift = cloud.drift[p];
      if (sprite === undefined || drift === undefined) continue;
      // Scattered through the lower half of the sphere: a canister lies on the
      // ground and the cloud builds upward from it.
      const ang = Math.random() * Math.PI * 2;
      const rad = radiusM * 0.65 * Math.sqrt(Math.random());
      sprite.position.set(
        at.x + Math.cos(ang) * rad,
        at.y + radiusM * 0.15 + Math.random() * radiusM * 0.5,
        at.z + Math.sin(ang) * rad,
      );
      sprite.scale.setScalar(radiusM * 0.2);
      sprite.visible = true;
      drift.x = (Math.random() - 0.5) * 0.25;
      drift.y = 0.12 + Math.random() * 0.18;
      drift.z = (Math.random() - 0.5) * 0.25;
      const mat = cloud.materials[p];
      if (mat !== undefined) mat.opacity = 0;
    }
  }

  /**
   * A window pane going: fourteen splinters off `at`, thrown BOTH WAYS along
   * the pane's normal (`axis` is its thin axis) so it reads as a break rather
   * than a spray, plus sideways spread and gravity.
   */
  shatter(at: Vec3, axis: "x" | "z"): void {
    const i = this.claimShatter();
    const sh = this.shatters[i];
    if (sh === undefined) return;

    sh.life = SHATTER_LIFE_MS;
    sh.born = ++this.stamp;

    for (let s = 0; s < sh.sprites.length; s++) {
      const sprite = sh.sprites[s];
      const vel = sh.vel[s];
      const mat = sh.materials[s];
      if (sprite === undefined || vel === undefined) continue;

      // Scattered over the pane's own face, so the burst starts as a sheet.
      const across = (Math.random() - 0.5) * 0.9;
      sprite.position.set(
        at.x + (axis === "x" ? 0 : across),
        at.y + (Math.random() - 0.5) * 1.1,
        at.z + (axis === "x" ? across : 0),
      );
      sprite.scale.setScalar(
        SHARD_MIN_M + Math.random() * (SHARD_MAX_M - SHARD_MIN_M),
      );
      sprite.material.rotation = Math.random() * Math.PI;
      sprite.visible = true;
      if (mat !== undefined) mat.opacity = SHARD_OPACITY;

      const through =
        (Math.random() < 0.5 ? -1 : 1) *
        (SHARD_NORMAL_MIN_MPS +
          Math.random() * (SHARD_NORMAL_MAX_MPS - SHARD_NORMAL_MIN_MPS));
      const side = (Math.random() - 0.5) * 2 * SHARD_SIDE_MPS;
      vel.x = axis === "x" ? through : side;
      vel.y = Math.random() * SHARD_SIDE_MPS;
      vel.z = axis === "x" ? side : through;
      sh.spin[s] = (Math.random() - 0.5) * 2 * SHARD_SPIN;
    }
  }

  /**
   * Puts out every cloud at once — leaving a raid, so a cloud does not hang in
   * the menu's void.
   */
  clearSmoke(): void {
    for (const cloud of this.smoke) {
      cloud.life = 0;
      for (const sprite of cloud.sprites) sprite.visible = false;
      for (const mat of cloud.materials) mat.opacity = 0;
    }
  }

  /** Fires the muzzle flash. No-op when no muzzle anchor was supplied. */
  muzzleFlash(): void {
    if (this.flashSprite === null) return;
    this.flashLife = FLASH_LIFE_MS;
    this.flashSprite.visible = true;
    // A little size jitter so a burst does not look like one frame repeated.
    this.flashSprite.scale.setScalar(0.13 + Math.random() * 0.06);
    this.flashSprite.material.rotation = Math.random() * Math.PI;
  }

  /**
   * Advances the timed pools; once per rendered frame. Bullets have no lifetime
   * of their own, only an owner.
   */
  update(dtMs: number): void {
    const dt = Math.min(Math.max(dtMs, 0), 100);

    // --- Impacts -----------------------------------------------------------
    for (const im of this.impacts) {
      if (im.life <= 0) continue;
      im.life -= dt;
      if (im.life <= 0) {
        im.life = 0;
        im.sprite.visible = false;
        im.material.opacity = 0;
        continue;
      }
      const k = 1 - im.life / IMPACT_LIFE_MS;
      im.sprite.scale.setScalar(
        IMPACT_START_M + (IMPACT_END_M - IMPACT_START_M) * k,
      );
      // Fades out faster than it grows, so the puff dissipates rather than
      // simply vanishing at full brightness.
      im.material.opacity = 0.75 * (1 - k) * (1 - k);
    }

    // --- Explosions --------------------------------------------------------
    const dtSec = dt / 1000;
    for (const ex of this.explosions) {
      if (ex.life <= 0) continue;
      ex.life -= dt;
      if (ex.life <= 0) {
        ex.life = 0;
        ex.core.visible = false;
        ex.coreMat.opacity = 0;
        for (let s = 0; s < ex.sparks.length; s++) {
          const sprite = ex.sparks[s];
          const mat = ex.sparkMats[s];
          if (sprite !== undefined) sprite.visible = false;
          if (mat !== undefined) mat.opacity = 0;
        }
        continue;
      }
      const k = 1 - ex.life / EXPLOSION_LIFE_MS;
      // The core expands fast and dies fast: most of its life is over in the
      // first eighty milliseconds, which is what makes it read as a bang.
      ex.core.scale.setScalar(
        EXPLOSION_CORE_START_M +
          (EXPLOSION_CORE_END_M - EXPLOSION_CORE_START_M) * Math.sqrt(k),
      );
      ex.coreMat.opacity = (1 - k) * (1 - k);

      for (let s = 0; s < ex.sparks.length; s++) {
        const sprite = ex.sparks[s];
        const vel = ex.sparkVel[s];
        const mat = ex.sparkMats[s];
        if (sprite === undefined || vel === undefined) continue;
        vel.y -= SPARK_GRAVITY * dtSec;
        sprite.position.x += vel.x * dtSec;
        sprite.position.y += vel.y * dtSec;
        sprite.position.z += vel.z * dtSec;
        if (mat !== undefined) mat.opacity = 1 - k;
      }
    }

    if (this.explosionLightLife > 0) {
      this.explosionLightLife -= dt;
      const k = this.explosionLightLife > 0 ? this.explosionLightLife / EXPLOSION_LIGHT_MS : 0;
      // Squared, so the light SLAMS off rather than dimming out — a blast is
      // over long before its smoke is.
      this.explosionLight.intensity = EXPLOSION_LIGHT_INTENSITY * k * k;
      if (this.explosionLightLife <= 0) this.explosionLightLife = 0;
    }

    // --- Smoke -------------------------------------------------------------
    for (const cloud of this.smoke) {
      if (cloud.life <= 0) continue;
      cloud.life -= dtSec;
      if (cloud.life <= 0) {
        cloud.life = 0;
        for (const sprite of cloud.sprites) sprite.visible = false;
        for (const mat of cloud.materials) mat.opacity = 0;
        continue;
      }
      const elapsed = cloud.totalSec - cloud.life;
      // Blooms in, holds, thins out — both ends ramps, not switches.
      const bloom = Math.min(1, elapsed / SMOKE_BLOOM_SEC);
      const fade = Math.min(1, cloud.life / SMOKE_FADE_SEC);
      const alpha = SMOKE_OPACITY * bloom * fade;
      const size = cloud.radiusM * (0.2 + 0.55 * bloom);
      for (let p = 0; p < cloud.sprites.length; p++) {
        const sprite = cloud.sprites[p];
        const drift = cloud.drift[p];
        const mat = cloud.materials[p];
        if (sprite === undefined || drift === undefined) continue;
        sprite.position.x += drift.x * dtSec;
        sprite.position.y += drift.y * dtSec;
        sprite.position.z += drift.z * dtSec;
        sprite.scale.setScalar(size);
        if (mat !== undefined) mat.opacity = alpha;
      }
    }

    // --- Breaking glass ----------------------------------------------------
    for (const sh of this.shatters) {
      if (sh.life <= 0) continue;
      sh.life -= dt;
      if (sh.life <= 0) {
        sh.life = 0;
        for (const sprite of sh.sprites) sprite.visible = false;
        for (const mat of sh.materials) mat.opacity = 0;
        continue;
      }
      const k = 1 - sh.life / SHATTER_LIFE_MS;
      const alpha = SHARD_OPACITY * (1 - k);
      for (let s = 0; s < sh.sprites.length; s++) {
        const sprite = sh.sprites[s];
        const vel = sh.vel[s];
        const mat = sh.materials[s];
        if (sprite === undefined || vel === undefined) continue;
        vel.y -= SHARD_GRAVITY * dtSec;
        sprite.position.x += vel.x * dtSec;
        sprite.position.y += vel.y * dtSec;
        sprite.position.z += vel.z * dtSec;
        sprite.material.rotation += (sh.spin[s] ?? 0) * dtSec;
        if (mat !== undefined) mat.opacity = alpha;
      }
    }

    // --- Muzzle flash ------------------------------------------------------
    if (this.flashLife > 0) {
      this.flashLife -= dt;
      const k = this.flashLife > 0 ? this.flashLife / FLASH_LIFE_MS : 0;
      if (this.flashSprite !== null) this.flashSprite.visible = k > 0;
      if (this.flashMat !== null) this.flashMat.opacity = k;
      if (this.flashLight !== null) {
        this.flashLight.intensity = MUZZLE_LIGHT_INTENSITY * k;
      }
      if (this.flashLife <= 0) this.flashLife = 0;
    }
  }

  dispose(): void {
    this.scene.remove(this.bulletMesh);
    this.scene.remove(this.impactRoot);
    this.scene.remove(this.blastRoot);
    this.scene.remove(this.smokeRoot);
    this.scene.remove(this.shardRoot);
    this.bulletGeo.dispose();
    this.bulletMat.dispose();
    for (const im of this.impacts) im.material.dispose();
    this.impactRoot.clear();
    this.impacts.length = 0;

    for (const ex of this.explosions) {
      ex.coreMat.dispose();
      for (const mat of ex.sparkMats) mat.dispose();
    }
    this.explosionLight.dispose();
    this.blastRoot.clear();
    this.explosions.length = 0;

    for (const cloud of this.smoke) {
      for (const mat of cloud.materials) mat.dispose();
    }
    this.smokeRoot.clear();
    this.smoke.length = 0;

    for (const sh of this.shatters) {
      for (const mat of sh.materials) mat.dispose();
    }
    this.shardRoot.clear();
    this.shatters.length = 0;

    if (this.flashSprite !== null) this.flashSprite.removeFromParent();
    if (this.flashLight !== null) {
      this.flashLight.removeFromParent();
      this.flashLight.dispose();
    }
    this.flashMat?.dispose();
    this.dustTexture?.dispose();
  }

  // -----------------------------------------------------------------------

  /** Oldest-wins recycling, exactly as `claimImpact` does it. */
  private claimExplosion(): number {
    let oldest = 0;
    let oldestBorn = Infinity;
    for (let i = 0; i < this.explosions.length; i++) {
      const ex = this.explosions[i];
      if (ex === undefined) continue;
      if (ex.life <= 0) return i;
      if (ex.born < oldestBorn) {
        oldestBorn = ex.born;
        oldest = i;
      }
    }
    return oldest;
  }

  private claimShatter(): number {
    let oldest = 0;
    let oldestBorn = Infinity;
    for (let i = 0; i < this.shatters.length; i++) {
      const sh = this.shatters[i];
      if (sh === undefined) continue;
      if (sh.life <= 0) return i;
      if (sh.born < oldestBorn) {
        oldestBorn = sh.born;
        oldest = i;
      }
    }
    return oldest;
  }

  private claimSmoke(): number {
    let oldest = 0;
    let oldestBorn = Infinity;
    for (let i = 0; i < this.smoke.length; i++) {
      const cloud = this.smoke[i];
      if (cloud === undefined) continue;
      if (cloud.life <= 0) return i;
      if (cloud.born < oldestBorn) {
        oldestBorn = cloud.born;
        oldest = i;
      }
    }
    return oldest;
  }

  private claimImpact(): number {
    let oldest = 0;
    let oldestBorn = Infinity;
    for (let i = 0; i < this.impacts.length; i++) {
      const im = this.impacts[i];
      if (im === undefined) continue;
      if (im.life <= 0) return i;
      if (im.born < oldestBorn) {
        oldestBorn = im.born;
        oldest = i;
      }
    }
    return oldest;
  }
}

/**
 * A soft radial dot for the dust puff, the muzzle flash and the laser's mark
 * (`game/tacticalFx.ts`). Each caller gets its own `CanvasTexture` and disposes
 * it — a shared one disposed by whoever finished first is a map-change bug.
 * `null` without a DOM.
 */
export function makeSoftDotTexture(): THREE.Texture | null {
  if (typeof document === "undefined") return null;

  const SIZE = 64;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;

  const g = ctx.createRadialGradient(
    SIZE / 2,
    SIZE / 2,
    0,
    SIZE / 2,
    SIZE / 2,
    SIZE / 2,
  );
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.45, "rgba(255,255,255,0.55)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, SIZE, SIZE);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  return tex;
}
